import { useConfig } from "@/context/ConfigContext";
import { useEffect, useState } from "react";

/**
 * Hook untuk mengambil product variants dari API
 * @param {Object} options - Options untuk fetch
 * @param {number} options.productId - ID produk (required)
 * @param {boolean} options.enabled - Enable/disable fetch (default: true)
 */
export function useProductVariants(options = {}) {
  const { productSlug, enabled = true } = options;
  const { config, configData } = useConfig();
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Pastikan config sudah ready dan productSlug tersedia
    if (!enabled || !productSlug || !configData?.team_id) {
      return;
    }

    const fetchVariants = async () => {
      try {
        setLoading(true);
        setError(null);

        const teamId = configData.team_id;

        // Gunakan proxy API route untuk menghindari CORS
        const proxyUrl = `/api/products?team_id=${teamId}&type=variants&product_id=${productSlug}&page=1&per_page=100`;

        console.log("[useProductVariants] Fetching variants:", {
          proxyUrl,
          teamId,
          productSlug,
        });

        const response = await fetch(proxyUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          cache: "no-cache",
        });

        if (!response.ok) {
          let errorData;
          let errorText;

          try {
            errorText = await response.text();
            errorData = JSON.parse(errorText);
          } catch (parseError) {
            errorData = {
              message: errorText || response.statusText,
              status: response.status,
            };
          }

          const apiUrl = errorData.apiUrl || "N/A";

          console.error("[useProductVariants] API Error:", {
            proxyEndpoint: proxyUrl,
            status: response.status,
            productSlug: productSlug,
            errorMessage: errorData.message || errorText || response.statusText,
          });

          throw new Error(
            errorData.message || errorText || response.statusText
          );
        }

        const data = await response.json();

        // Handle response - variants ada di data.data.variants
        if (data.data && data.data.variants && Array.isArray(data.data.variants)) {
          // Filter hanya variant yang aktif
          const activeVariants = data.data.variants.filter(
            (variant) => variant.is_active !== false
          );
          setVariants(activeVariants);
        } else if (Array.isArray(data.variants)) {
          const activeVariants = data.variants.filter(
            (variant) => variant.is_active !== false
          );
          setVariants(activeVariants);
        } else if (Array.isArray(data.data)) {
          const activeVariants = data.data.filter(
            (variant) => variant.is_active !== false
          );
          setVariants(activeVariants);
        } else {
          setVariants([]);
        }
      } catch (err) {
        console.error("[useProductVariants] Error fetching variants:", {
          message: err.message,
          productSlug: productSlug,
        });
        setError(err.message || "Gagal mengambil variants");
      } finally {
        setLoading(false);
        setFetched(true);
      }
    };

    fetchVariants();
  }, [configData?.team_id, productSlug, enabled]);

  return {
    variants,
    fetched,
    loading,
    error,
  };
}

